/**
 * Commercial model of Kairos Merit: plans, their entitlements per billing mode
 * and the add-on catalog. Source: client spec of 9-ago + the decisions of
 * 10-ago (`docs/cliente/2026-08-10_MDs/MDs/MD-Planes-Configuracion.md`).
 *
 * This file is the SEED and the fallback, never the enforced value: the
 * authority is the `plans` / `plan_pricing` / `plan_entitlements` / `addons`
 * tables, which the admin edits from Configuración. Same contract the older
 * `SUBSCRIPTION_PLANS` had with `PlanCatalogService` — the constant exists so an
 * unseeded database still renders, not so the numbers live in code.
 *
 * What changed against the previous model, because it is not a re-tune of
 * numbers but a different shape:
 *
 * - Four sold plans, and the legacy names are gone: Essential → ACCESS,
 *   Platinum → PRIME, plus ENTERPRISE (contact sales, no inherited limits). The
 *   fifth entry, FREEMIUM (13-ago), is not sold: it is what a company gets by
 *   registering, and it is hidden from the pricing screens.
 * - Two billing modes with their OWN prices and their OWN entitlements. Annual
 *   is not monthly × 12: ACCESS allows 1 simultaneous vacancy monthly and 2
 *   annually.
 * - Volume and simultaneity are different counters. A Vacancy Credit is the
 *   right to activate a vacancy (consumed, never returned); Active Vacancies is
 *   how many may be open at the same time (freed when one closes).
 * - Invitations are a GLOBAL credit of the plan. The old cap of 15 per vacancy
 *   is dropped (decision of 10-ago).
 * - Each plan also limits WHICH seniority levels it may publish and how many of
 *   the high ones (`seniorityAllowance`, Jumeirah 13-ago).
 */
import type { SeniorityLevel } from './vacancy-criteria.dto';

/** Every level allowed, no cap — what a plan with no seniority restriction gets. */
const ANY_SENIORITY: SeniorityAllowance = {
	operational: null,
	supervisor: null,
	management: null,
	senior_director: null,
};

/**
 * The matrix reads "Sí / No / máx. N", and this turns it into the file's own
 * language: unrestricted levels are `null`, forbidden ones `0`.
 */
function seniorityAllowance(management: number, seniorDirector: number, supervisor: 'yes' | 'no'): SeniorityAllowance {
	return {
		operational: null,
		supervisor: supervisor === 'yes' ? null : 0,
		management,
		senior_director: seniorDirector,
	};
}

/**
 * Plan identity. `enterprise` is quoted per contract, not priced here.
 *
 * `freemium` is the free tier of 13-ago (`MD-Freemium-Empresa`). It is modelled
 * as one more plan and NOT as a parallel `tier` column on the company: the MD
 * words it as a tier, but a second field saying which limits apply would be a
 * second answer to the question `plan_entitlements` already answers, and the two
 * would drift the first time a company converted. A freemium company is simply a
 * company with no subscription — which is also what makes it the safe default in
 * {@link resolveCompanyPlanKey}.
 */
export const PLAN_KEYS = ['freemium', 'access', 'professional', 'prime', 'enterprise'] as const;
export type PlanKey = (typeof PLAN_KEYS)[number];

/** Billing mode. `monthly` runs on a 30-day cycle, `annual` on 12 months. */
export const BILLING_MODES = ['monthly', 'annual'] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

/**
 * Badge shown on the pricing card and the tier used to resolve how long a plan
 * waits before eligible talent becomes visible to it (see
 * {@link PlanEntitlementValues.priorityDelayHours}).
 */
export const PRIORITY_TIERS = ['first', 'priority', 'standard'] as const;
export type PriorityTier = (typeof PRIORITY_TIERS)[number];

/**
 * What buying an add-on actually grants. The consumption ledger keys off this,
 * so an add-on with no effect is a line on an invoice and nothing else.
 *
 * `extension` is kept for the vacancy extension the client shelved on 10-ago:
 * the vacancy now closes at day 30 with no upsell. The effect stays modelled so
 * reviving it is a row, not a migration — but NO add-on uses it today and none
 * must be shown in the frontend.
 */
export const ADDON_EFFECTS = [
	'vacancy',
	'contact_unlock_pack',
	'invitation_pack',
	'video_pack',
	'shortlist',
	'seat',
	'venue',
	'extension',
	// Un Private Talent Desk POR CATEGORÍA (precios de la clienta, 02/09).
	// Estos efectos NO conceden saldo en el ledger a propósito: la compra sin
	// consumir ES el crédito (patrón `early_access_window`), y pedir el PTD de
	// una vacante de esa categoría lo consume marcando `consumedAt`. Un crédito
	// de Supervisor no vale para una vacante Management — regla confirmada.
	'shortlist_operational',
	'shortlist_supervisor',
	'shortlist_management',
	'shortlist_senior_director',
] as const;
export type AddonEffect = (typeof ADDON_EFFECTS)[number];

/**
 * Entitlements the ledger meters. Every consumption in the app emits one of
 * these, and the usage dashboard, the talent screen and the backend all read
 * the same rows — the QA requirement is that the balance matches everywhere.
 */
export const ENTITLEMENT_KEYS = [
	'vacancy_credit',
	'active_vacancy',
	'contact_unlock',
	'invitation',
	'video_request',
	'shortlist',
	'seat',
	'venue',
	// One per seniority level (Jumeirah 13-ago). These do NOT replace
	// `vacancy_credit`: publishing always spends a credit, and a Management or
	// Senior/Director role spends one of these on top. They live in the same
	// ledger as everything else because the alternative — the `vacancy_seniority_usage`
	// table the MD suggests — would be a second place answering "how many has this
	// company published", and two counters for one question drift on the first
	// upgrade. The MD only asks for the two high levels; all four exist so mapping
	// a level to its key is a total function with no special cases, and only the
	// metered ones ever get an event written.
	'operational_vacancy',
	'supervisor_vacancy',
	'management_vacancy',
	'senior_director_vacancy',
] as const;
export type EntitlementKey = (typeof ENTITLEMENT_KEYS)[number];

/** The ledger key that meters a seniority level. */
export function seniorityEntitlementKey(level: SeniorityLevel): EntitlementKey {
	return `${level}_vacancy` as EntitlementKey;
}

/** El efecto del add-on que compra un Private Talent Desk de esa categoría. */
export function ptdAddonEffect(level: SeniorityLevel): AddonEffect {
	return `shortlist_${level}` as AddonEffect;
}

/**
 * The numbers a plan grants for one billing mode.
 *
 * `null` means "not capped": unlimited for PRIME's video requests, and "per
 * contract" for every ENTERPRISE field. `0` is the opposite — included at zero,
 * which is how ACCESS has no video requests at all. The distinction is load
 * bearing: a null read as 0 would lock PRIME out of the feature it pays for.
 */
export interface PlanEntitlementValues {
	/** Right to activate a vacancy. Volume over the cycle; not returned on close. */
	vacancyCredits: number | null;
	/** Vacancies open at the same time. Freed when one closes. */
	activeVacancies: number | null;
	/** Reveals of a candidate's identifying data. Once per candidate/company. */
	contactUnlocks: number | null;
	/** Global invitation credits — any vacancy draws from the same balance. */
	invitations: number | null;
	/** Video presentations requested on demand. `null` = unlimited (fair use). */
	videoRequests: number | null;
	/** Kairos shortlists included (PRIME only). */
	shortlists: number | null;
	/** Candidates delivered per shortlist. */
	shortlistCandidates: number;
	/** Company users with workspace access. */
	seats: number | null;
	/** Venues covered by the subscription. */
	venues: number | null;
	/** Messaging with candidates. FREEMIUM has none (`MD-Freemium-Empresa §4.9`). */
	chat: boolean;
	/** Badge on the pricing card. */
	priority: PriorityTier;
	/**
	 * Hours an eligible candidate stays invisible to this plan: PRIME sees them
	 * at once (0), PROFESSIONAL after 72h, ACCESS after 96h. The spec words it
	 * the other way round ("PRIME: first 0–72h"), but the delay is what the
	 * query needs and it expresses all three tiers with one number.
	 */
	priorityDelayHours: number;
	/** Days of replacement coverage, counted from the effective start date. */
	replacementDays: number;
	/**
	 * How many vacancies of each seniority level this plan may publish
	 * (`MD-Planes-Configuracion §6bis`, Jumeirah 13-ago). Same `null` = unlimited,
	 * `0` = not included as every other entitlement here.
	 *
	 * The MD words it differently — two booleans for the low levels and
	 * `null = no permitido` for the high ones — and that was not adopted on
	 * purpose: a field where `null` means the OPPOSITE of `null` in the eleven
	 * fields above it is how PRIME ends up with unlimited directors. One concept,
	 * one rule. The matrix is expressed exactly the same either way.
	 *
	 * These are **controls, not credits**: publishing always costs one Vacancy
	 * Credit, and Management/Senior spend one of these *as well*.
	 */
	seniorityAllowance: SeniorityAllowance;
	/**
	 * What a one-off Private Talent Desk costs this plan in this mode, per
	 * seniority of the vacancy (clienta, 04/09). `null`/absent = the general
	 * price list (`ptd_*` packs). Not an add-on: bought from the vacancy's PTD.
	 */
	ptdOperationalAed?: number | null;
	ptdSupervisorAed?: number | null;
	ptdManagementAed?: number | null;
	ptdSeniorDirectorAed?: number | null;
}

/** The entitlement column that prices a one-off PTD for a seniority level. */
export function ptdPriceField(level: SeniorityLevel): keyof PlanEntitlementValues {
	switch (level) {
		case 'operational':
			return 'ptdOperationalAed';
		case 'supervisor':
			return 'ptdSupervisorAed';
		case 'management':
			return 'ptdManagementAed';
		default:
			return 'ptdSeniorDirectorAed';
	}
}

/** Per-level publishing allowance. `null` = unlimited, `0` = this plan cannot publish it. */
export type SeniorityAllowance = Record<SeniorityLevel, number | null>;

/** Ceilings that force the conversation up to ENTERPRISE instead of more add-ons. */
export interface PlanCapValues {
	vacancies: number | null;
	contactUnlocks: number | null;
	invitations: number | null;
	shortlists: number | null;
	seats: number | null;
	venues: number | null;
}

export interface PlanPricingEntry {
	/** AED. `null` = quoted (ENTERPRISE). */
	priceAed: number | null;
	/** Length of a monthly cycle. */
	cycleDays: number | null;
	/** Length of an annual commitment. */
	durationMonths: number | null;
	entitlements: PlanEntitlementValues;
	caps: PlanCapValues | null;
}

export interface PlanCatalogSeed {
	key: PlanKey;
	name: string;
	order: number;
	recommended: boolean;
	/** Entitlements come from the signed contract, not from this catalog. */
	isEnterprise: boolean;
	/** Card shows "Contact Sales" instead of a price and a checkout button. */
	contactSales: boolean;
	/**
	 * Kept out of the pricing screens (`MD-Freemium-Empresa §2`): FREEMIUM is the
	 * state a company lands in by registering, not a product competing with
	 * ACCESS/PROFESSIONAL/PRIME on the cards. It is a flag and not "the plan whose
	 * price is 0" because a free promotional plan could perfectly well be sold one
	 * day, and because the pricing screens must never have to know a plan by name.
	 */
	notListedInPricing: boolean;
	pricing: Record<BillingMode, PlanPricingEntry>;
}

const ENTERPRISE_ENTITLEMENTS: PlanEntitlementValues = {
	vacancyCredits: null,
	activeVacancies: null,
	contactUnlocks: null,
	invitations: null,
	videoRequests: null,
	shortlists: null,
	shortlistCandidates: 3,
	seats: null,
	venues: null,
	chat: true,
	priority: 'first',
	priorityDelayHours: 0,
	replacementDays: 0,
	// Per contract, like every other ENTERPRISE field.
	seniorityAllowance: ANY_SENIORITY,
};

/**
 * What registering for free grants (`MD-Freemium-Empresa §4`). Everything the
 * company would have to pay for is `0` and not `null`: on this scale `null`
 * means "not capped", so a null video allowance would hand the free tier the
 * unlimited one PRIME pays for.
 *
 * `vacancyCredits` is 5 for the whole free year (client, 14/10). Publishing
 * spends one and closing, pausing or deleting does not give it back — the same
 * ledger the paid plans already use for their period. `activeVacancies` stays
 * 5: that is how many may be open at once, and closing still frees that slot.
 * The window is the free year (`freemiumStartedAt`), which does not refill.
 *
 * `invitations: 0` follows from rule 12, not from the invitation rules: an
 * invitation reaches the candidate with the company's name on it, and a freemium
 * company is invisible to candidates until it subscribes.
 */
const FREEMIUM_ENTITLEMENTS: PlanEntitlementValues = {
	vacancyCredits: 5,
	activeVacancies: 5,
	contactUnlocks: 0,
	invitations: 0,
	videoRequests: 0,
	shortlists: 0,
	shortlistCandidates: 3,
	seats: 1,
	venues: 1,
	chat: false,
	priority: 'standard',
	// Same delay as ACCESS. The Premium Talent Window is gated separately (the
	// `window_72h.plansWithAccess` setting, which does not list freemium), so this
	// only has to avoid being the tier that sees talent first.
	priorityDelayHours: 96,
	replacementDays: 0,
	// Freemium publishes any level, "como PRIME" (`MD-Freemium-Empresa`,
	// ACTUALIZACIÓN 13-ago). It costs nothing commercially because it never sees
	// a contact detail — and it is what shows the company what PRIME buys.
	seniorityAllowance: ANY_SENIORITY,
};

export const PLAN_CATALOG: PlanCatalogSeed[] = [
	{
		key: 'freemium',
		name: 'Freemium',
		order: 0,
		recommended: false,
		isEnterprise: false,
		contactSales: false,
		notListedInPricing: true,
		// Both modes exist and are identical: a freemium company has no
		// subscription, so the resolver reads it as `annual`, and leaving the
		// monthly row out would make it a plan "not sold monthly" — a shape the
		// catalog readers legitimately use to hide things.
		pricing: {
			monthly: {
				priceAed: 0,
				cycleDays: 30,
				durationMonths: null,
				caps: null,
				entitlements: FREEMIUM_ENTITLEMENTS,
			},
			annual: {
				priceAed: 0,
				cycleDays: null,
				durationMonths: 12,
				caps: null,
				entitlements: FREEMIUM_ENTITLEMENTS,
			},
		},
	},
	{
		key: 'access',
		name: 'Access',
		order: 1,
		recommended: false,
		isEnterprise: false,
		contactSales: false,
		notListedInPricing: false,
		pricing: {
			monthly: {
				priceAed: 4125,
				cycleDays: 30,
				durationMonths: null,
				caps: null,
				entitlements: {
					vacancyCredits: 1,
					activeVacancies: 1,
					contactUnlocks: 5,
					invitations: 18,
					videoRequests: 0,
					shortlists: 0,
					shortlistCandidates: 3,
					seats: 1,
					venues: 1,
					chat: true,
					priority: 'standard',
					priorityDelayHours: 96,
					replacementDays: 0,
					// ACCESS publica solo Operational (matriz §6bis).
					seniorityAllowance: seniorityAllowance(0, 0, 'no'),
				},
			},
			annual: {
				priceAed: 45000,
				cycleDays: null,
				durationMonths: 12,
				caps: null,
				entitlements: {
					vacancyCredits: 12,
					activeVacancies: 2,
					contactUnlocks: 72,
					invitations: 216,
					videoRequests: 0,
					shortlists: 0,
					shortlistCandidates: 3,
					seats: 1,
					venues: 1,
					chat: true,
					priority: 'standard',
					priorityDelayHours: 96,
					replacementDays: 0,
					// Los 12 creditos del ano son todos Operational.
					seniorityAllowance: seniorityAllowance(0, 0, 'no'),
				},
			},
		},
	},
	{
		key: 'professional',
		name: 'Professional',
		order: 2,
		recommended: false,
		isEnterprise: false,
		contactSales: false,
		notListedInPricing: false,
		pricing: {
			monthly: {
				priceAed: 5950,
				cycleDays: 30,
				durationMonths: null,
				caps: null,
				entitlements: {
					vacancyCredits: 2,
					activeVacancies: 2,
					contactUnlocks: 15,
					invitations: 60,
					videoRequests: 7,
					shortlists: 0,
					shortlistCandidates: 3,
					seats: 3,
					venues: 2,
					chat: true,
					priority: 'priority',
					priorityDelayHours: 72,
					replacementDays: 30,
					// Supervisor libre; 1 Management por ciclo; nada de Senior/Director.
					seniorityAllowance: seniorityAllowance(1, 0, 'yes'),
				},
			},
			annual: {
				priceAed: 65000,
				cycleDays: null,
				durationMonths: 12,
				caps: null,
				entitlements: {
					vacancyCredits: 30,
					activeVacancies: 5,
					contactUnlocks: 216,
					invitations: 720,
					videoRequests: 100,
					shortlists: 0,
					shortlistCandidates: 3,
					seats: 3,
					venues: 2,
					chat: true,
					priority: 'priority',
					priorityDelayHours: 72,
					replacementDays: 30,
					// 8 Management al ano dentro de los 30 creditos.
					seniorityAllowance: seniorityAllowance(8, 0, 'yes'),
				},
			},
		},
	},
	{
		key: 'prime',
		name: 'Prime',
		order: 3,
		recommended: true,
		isEnterprise: false,
		contactSales: false,
		notListedInPricing: false,
		pricing: {
			monthly: {
				priceAed: 7800,
				cycleDays: 30,
				durationMonths: null,
				caps: null,
				entitlements: {
					vacancyCredits: 4,
					activeVacancies: 4,
					contactUnlocks: 35,
					invitations: 150,
					videoRequests: null,
					shortlists: 2,
					shortlistCandidates: 3,
					seats: 5,
					venues: 4,
					chat: true,
					priority: 'first',
					priorityDelayHours: 0,
					replacementDays: 90,
					// El ejemplo valido de la clienta: 1 Director + 2 Managers + 1 Operational.
					seniorityAllowance: seniorityAllowance(2, 1, 'yes'),
				},
			},
			annual: {
				priceAed: 85000,
				cycleDays: null,
				durationMonths: 12,
				// Past this, add-ons stop being the answer and the account goes
				// to ENTERPRISE. Only the annual mode has a published ceiling.
				caps: {
					vacancies: 80,
					contactUnlocks: 700,
					invitations: 2200,
					shortlists: 48,
					seats: 8,
					venues: 6,
				},
				entitlements: {
					vacancyCredits: 60,
					activeVacancies: 12,
					contactUnlocks: 500,
					invitations: 1800,
					videoRequests: null,
					shortlists: 36,
					shortlistCandidates: 3,
					seats: 5,
					venues: 4,
					chat: true,
					priority: 'first',
					priorityDelayHours: 0,
					replacementDays: 90,
					// 20 Management y 8 Senior/Director al ano.
					seniorityAllowance: seniorityAllowance(20, 8, 'yes'),
				},
			},
		},
	},
	{
		key: 'enterprise',
		name: 'Enterprise',
		order: 4,
		recommended: false,
		isEnterprise: true,
		contactSales: true,
		notListedInPricing: false,
		pricing: {
			monthly: {
				priceAed: null,
				cycleDays: 30,
				durationMonths: null,
				caps: null,
				entitlements: ENTERPRISE_ENTITLEMENTS,
			},
			annual: {
				priceAed: null,
				cycleDays: null,
				durationMonths: 12,
				caps: null,
				entitlements: ENTERPRISE_ENTITLEMENTS,
			},
		},
	},
];

export interface AddonCatalogSeed {
	/** Plan-scoped: the same extra costs a different price on each plan. */
	key: string;
	planKey: PlanKey | null;
	name: string;
	effect: AddonEffect;
	/** Units granted per purchase — 10 unlocks, 25 invitations, 1 seat… */
	quantity: number;
	/** Price on a monthly subscription. `null` = not sold monthly. */
	priceAed: number | null;
	/** Price on an annual subscription. `null` = not sold annually. */
	priceAnnualAed: number | null;
	/** Purchases allowed per monthly cycle. `null` = not offered monthly. */
	cycleLimit: number | null;
	/** Purchases allowed per annual term. `null` = not offered annually. */
	annualLimit: number | null;
	order: number;
}

/**
 * Add-on catalog (spec §7). Keys are prefixed with the plan because the price
 * and the limits differ per plan — an extra vacancy is 1.500 on ACCESS and
 * 1.000 on PRIME, and `addons.key` is unique.
 *
 * No vacancy-extension add-on: shelved on 10-ago, the vacancy closes at day 30.
 */
export const ADDON_CATALOG: AddonCatalogSeed[] = [
	// ---- ACCESS ----
	{
		key: 'access_additional_vacancy',
		planKey: 'access',
		name: 'Vacante adicional',
		effect: 'vacancy',
		quantity: 1,
		priceAed: 1500,
		priceAnnualAed: 1500,
		cycleLimit: 2,
		annualLimit: 6,
		order: 10,
	},
	{
		key: 'access_unlock_pack_5',
		planKey: 'access',
		name: '+5 Contact Unlocks',
		effect: 'contact_unlock_pack',
		quantity: 5,
		priceAed: 750,
		priceAnnualAed: null,
		cycleLimit: 1,
		annualLimit: null,
		order: 11,
	},
	{
		key: 'access_unlock_pack_10',
		planKey: 'access',
		name: '+10 Contact Unlocks',
		effect: 'contact_unlock_pack',
		quantity: 10,
		priceAed: null,
		priceAnnualAed: 1250,
		cycleLimit: null,
		annualLimit: 3,
		order: 12,
	},
	{
		key: 'access_additional_seat',
		planKey: 'access',
		name: 'Usuario adicional',
		effect: 'seat',
		quantity: 1,
		priceAed: 750,
		priceAnnualAed: 7500,
		cycleLimit: 1,
		annualLimit: 1,
		order: 13,
	},

	// ---- PROFESSIONAL ----
	{
		key: 'professional_additional_vacancy',
		planKey: 'professional',
		name: 'Vacante adicional',
		effect: 'vacancy',
		quantity: 1,
		priceAed: 1250,
		priceAnnualAed: 1250,
		cycleLimit: 3,
		annualLimit: 12,
		order: 20,
	},
	{
		key: 'professional_unlock_pack_10',
		planKey: 'professional',
		name: '+10 Contact Unlocks',
		effect: 'contact_unlock_pack',
		quantity: 10,
		priceAed: 1000,
		priceAnnualAed: null,
		cycleLimit: 2,
		annualLimit: null,
		order: 21,
	},
	{
		key: 'professional_unlock_pack_20',
		planKey: 'professional',
		name: '+20 Contact Unlocks',
		effect: 'contact_unlock_pack',
		quantity: 20,
		priceAed: null,
		priceAnnualAed: 1750,
		cycleLimit: null,
		annualLimit: 4,
		order: 22,
	},
	{
		key: 'professional_invitation_pack_25',
		planKey: 'professional',
		name: '+25 Invitaciones',
		effect: 'invitation_pack',
		quantity: 25,
		priceAed: 500,
		priceAnnualAed: null,
		cycleLimit: 2,
		annualLimit: null,
		order: 23,
	},
	{
		key: 'professional_invitation_pack_50',
		planKey: 'professional',
		name: '+50 Invitaciones',
		effect: 'invitation_pack',
		quantity: 50,
		priceAed: null,
		priceAnnualAed: 900,
		cycleLimit: null,
		annualLimit: 4,
		order: 24,
	},
	{
		key: 'professional_video_pack_5',
		planKey: 'professional',
		name: '+5 Solicitudes de vídeo',
		effect: 'video_pack',
		quantity: 5,
		priceAed: 500,
		priceAnnualAed: null,
		cycleLimit: 1,
		annualLimit: null,
		order: 25,
	},
	{
		key: 'professional_video_pack_10',
		planKey: 'professional',
		name: '+10 Solicitudes de vídeo',
		effect: 'video_pack',
		quantity: 10,
		priceAed: null,
		priceAnnualAed: 900,
		cycleLimit: null,
		annualLimit: 4,
		order: 26,
	},
	{
		key: 'professional_additional_seat',
		planKey: 'professional',
		name: 'Usuario adicional',
		effect: 'seat',
		quantity: 1,
		priceAed: 600,
		priceAnnualAed: 6000,
		cycleLimit: 2,
		annualLimit: 2,
		order: 27,
	},
	{
		key: 'professional_additional_venue',
		planKey: 'professional',
		name: 'Establecimiento adicional',
		effect: 'venue',
		quantity: 1,
		priceAed: 1000,
		priceAnnualAed: 10000,
		cycleLimit: 1,
		annualLimit: 1,
		order: 28,
	},

	// ---- PRIME ----
	{
		key: 'prime_additional_vacancy',
		planKey: 'prime',
		name: 'Vacante adicional',
		effect: 'vacancy',
		quantity: 1,
		priceAed: 1000,
		priceAnnualAed: 1000,
		cycleLimit: 4,
		annualLimit: 20,
		order: 30,
	},
	{
		key: 'prime_unlock_pack_20',
		planKey: 'prime',
		name: '+20 Contact Unlocks',
		effect: 'contact_unlock_pack',
		quantity: 20,
		priceAed: 1500,
		priceAnnualAed: null,
		cycleLimit: 2,
		annualLimit: null,
		order: 31,
	},
	{
		key: 'prime_unlock_pack_50',
		planKey: 'prime',
		name: '+50 Contact Unlocks',
		effect: 'contact_unlock_pack',
		quantity: 50,
		priceAed: null,
		priceAnnualAed: 3250,
		cycleLimit: null,
		annualLimit: 4,
		order: 32,
	},
	{
		key: 'prime_invitation_pack_50',
		planKey: 'prime',
		name: '+50 Invitaciones',
		effect: 'invitation_pack',
		quantity: 50,
		priceAed: 750,
		priceAnnualAed: null,
		cycleLimit: 2,
		annualLimit: null,
		order: 33,
	},
	{
		key: 'prime_invitation_pack_100',
		planKey: 'prime',
		name: '+100 Invitaciones',
		effect: 'invitation_pack',
		quantity: 100,
		priceAed: null,
		priceAnnualAed: 1250,
		cycleLimit: null,
		annualLimit: 4,
		order: 34,
	},
	{
		key: 'prime_additional_shortlist',
		planKey: 'prime',
		name: 'Private Talent Desk adicional · 3 candidatos',
		effect: 'shortlist',
		quantity: 1,
		priceAed: 1000,
		priceAnnualAed: 1000,
		cycleLimit: 2,
		annualLimit: 12,
		order: 35,
	},
	// La compra de un Private Talent Desk, por categoría del puesto y para TODOS
	// los planes («añádeme abajo donde las add-ons para comprar PTD en todos los
	// planes», 02/09). Los precios son los del documento de la clienta y se
	// editan desde Configuración → Planes, sin desplegar. El mismo precio en
	// mensual y anual: es un servicio suelto, no una suscripción.
	{
		key: 'ptd_operational',
		planKey: null,
		name: 'Private Talent Desk · Operational',
		effect: 'shortlist_operational',
		quantity: 1,
		priceAed: 3500,
		priceAnnualAed: 3500,
		cycleLimit: 99,
		annualLimit: 99,
		order: 37,
	},
	{
		key: 'ptd_supervisor',
		planKey: null,
		name: 'Private Talent Desk · Supervisor',
		effect: 'shortlist_supervisor',
		quantity: 1,
		priceAed: 5500,
		priceAnnualAed: 5500,
		cycleLimit: 99,
		annualLimit: 99,
		order: 38,
	},
	{
		key: 'ptd_management',
		planKey: null,
		name: 'Private Talent Desk · Management',
		effect: 'shortlist_management',
		quantity: 1,
		priceAed: 8500,
		priceAnnualAed: 8500,
		cycleLimit: 99,
		annualLimit: 99,
		order: 39,
	},
	{
		key: 'ptd_senior_director',
		planKey: null,
		name: 'Private Talent Desk · Senior / Director',
		effect: 'shortlist_senior_director',
		quantity: 1,
		priceAed: 12500,
		priceAnnualAed: 12500,
		cycleLimit: 99,
		annualLimit: 99,
		order: 40,
	},
	{
		key: 'prime_additional_seat',
		planKey: 'prime',
		name: 'Usuario adicional',
		effect: 'seat',
		quantity: 1,
		priceAed: 500,
		priceAnnualAed: 5000,
		cycleLimit: 3,
		annualLimit: 3,
		order: 36,
	},
	{
		key: 'prime_additional_venue',
		planKey: 'prime',
		name: 'Establecimiento adicional',
		effect: 'venue',
		quantity: 1,
		priceAed: 1250,
		priceAnnualAed: 12500,
		cycleLimit: 2,
		annualLimit: 2,
		order: 37,
	},

	// No "Destacar vacante" here: it was a product of the previous model that the
	// spec's §7 does not carry, and the client dropped it on 10-ago. The
	// `vacancies.featured` flag survives as an internal marker — an admin can
	// still highlight a role — but there is nothing to buy, so the monetization
	// screen reports that source as a module still to be built rather than as
	// zero revenue, which would read as "sold none" instead of "does not exist".
];

/**
 * Add-ons of the previous commercial model, superseded by the plan-scoped
 * catalog above (`express_shortlist`, `extra_role_slot`, `early_access_window`,
 * `extra_video_slot`).
 *
 * They are DELETED, not kept: the product is still in development, the client
 * confirmed on 10-ago that the old catalog goes, and leaving dead rows around
 * only invites someone to sell one. The seed removes any `addons` row whose key
 * is not in {@link ADDON_CATALOG}, so this list needs no maintenance — a name
 * dropped from the catalog disappears from the database on the next seed.
 *
 * `early_access_window` has no successor at all: the 72h window stopped being a
 * purchasable extra and became an entitlement of PRIME.
 */

/**
 * Plans of the previous model mapped onto the new ones, applied by the seed to
 * `plans`, `subscriptions.plan_key` and `companies.plan`. Without it a company
 * on `essential` would resolve to no plan at all and lose every limit.
 */
export const LEGACY_PLAN_KEY_MAP: Record<string, PlanKey> = {
	essential: 'access',
	platinum: 'prime',
};

/** Los dos estados de la ficha que activan los límites del plan pagado. */
const APPROVED_VALIDATION_STATES = new Set(['approved', 'approved_with_limits']);

/**
 * ¿Kairos ha aprobado a esta empresa en Scoring? Los mismos dos estados que abren
 * el plan pagado, y por eso se exporta el conjunto y no una copia: la analítica
 * (hito `account_activated`, 21/09) mide exactamente esta aprobación, y dos listas
 * dejarían de coincidir el día que se añada un tercer estado.
 */
export function isApprovedValidationState(state: string | null | undefined): boolean {
	return APPROVED_VALIDATION_STATES.has(state ?? '');
}

/**
 * The plan key in force for a company: its subscription if it has one, the plan
 * written on the company row otherwise, and FREEMIUM when it has neither.
 *
 * Six services spelled this out inline, all six ending in `?? 'access'`. That
 * default was wrong twice over once the free tier existed: it handed a company
 * that had never paid the limits of the entry plan — five Contact Unlocks a
 * month, for free — and it meant a company registering for free would be
 * browsing under ACCESS rules until someone remembered to write a plan on it.
 * No subscription and no plan is precisely the definition of freemium, so that
 * is what it now resolves to, in one place instead of six.
 *
 * **Y una empresa bajo revisión opera como freemium** (respuesta de la clienta,
 * 02/09, E-P0-03: «puede seguir viendo como si fuera freemium hasta ser
 * aceptada»). Un plan pagado no enciende sus límites hasta que la ficha esté
 * aprobada — antes de esto la revisión no gobernaba nada, y el primer arreglo
 * (cerrar el portal entero) dejaba fuera a quien acababa de pagar.
 *
 * `lead` es OBLIGATORIO en la firma a propósito: el sitio que lo omita no
 * compila, que es lo que impide que una pantalla nueva resuelva el plan sin
 * pasar por la regla — el mismo truco que `@SelfOrPermissions()` con los
 * campos. Una empresa sin ficha (`lead: null`) no se puede juzgar y conserva
 * su plan: dejarla sin límites por un hueco de datos sería un apagón, no una
 * salvaguarda.
 */
export function resolveCompanyPlanKey(company: {
	subscription?: { planKey: string; status?: string } | null;
	plan?: string | null;
	lead: { validationState: string | null } | null | undefined;
}): string {
	// Una suscripción que NO está activa —impago (`past_due`), cancelada— no
	// da su plan (03/09 · 24): hasta hoy una renovación fallida no degradaba a
	// nadie, porque esto miraba la clave del plan y nunca el estado. Sin
	// suscripción viva, la empresa ES freemium (regla del repo), y `company.plan`
	// es solo la etiqueta que lista el backoffice, no un derecho.
	if (company.subscription?.status && company.subscription.status !== 'active') {
		return 'freemium';
	}
	const raw = company.subscription?.planKey ?? company.plan ?? 'freemium';
	const lowered = raw.toLowerCase();
	const resolved = LEGACY_PLAN_KEY_MAP[lowered] ?? lowered;
	if (
		resolved !== 'freemium' &&
		company.lead != null &&
		!APPROVED_VALIDATION_STATES.has(company.lead.validationState ?? '')
	) {
		return 'freemium';
	}
	return resolved;
}
